import { useState } from 'react';
import { ADDONS, FEATURE_DETAIL, FEATURE_KEYS, formatStorage, PLAN_ORDER, PLANS, type AddonId, type FeatureKey, type PlanDef, type PlanId } from '@machs/shared';
import { api } from '../lib/api';
import { usePlan } from '../lib/plan';
import { Icon } from '../lib/icons';
import { Dialog, useToast } from './ui';

const FEATURES: FeatureKey[] = FEATURE_KEYS;

/** Grenzen eines Tarifs als kurze Zeilen */
function limitLines(p: PlanDef): string[] {
  const l = p.limits;
  const n = (v: number | null, one: string, many: string) => (v === null ? `Unbegrenzt ${many}` : `${v} ${v === 1 ? one : many}`);
  return [
    `${l.users} Mitglieder inklusive${p.price ? `, jedes weitere ${p.price.extraUser} €` : ''}`,
    l.businesses === null ? 'Mehrere Geschäfte' : 'Ein Geschäft',
    n(l.projects, 'Projekt', 'Projekte'),
    l.openTasks === null ? 'Unbegrenzt offene Aufgaben' : `${l.openTasks} offene Aufgaben`,
    `${formatStorage(l.storageMb ?? 0)} Speicher`,
    `Videos bis ${Math.round((l.videoSeconds ?? 0) / 60)} Minuten`,
    l.activeShares === null ? 'Unbegrenzt Freigaben für Externe' : `${l.activeShares} aktive Freigaben für Externe`,
    l.boardsPerProject === null ? 'Unbegrenzt Boards' : 'Ein Board je Projekt',
  ];
}

/**
 * Hinweis an einer Tarifgrenze und Tarifvergleich. Gebucht wird über Stripe
 * (nur im Web); Inhaber und Admins dürfen wählen.
 */
export function UpgradeDialog() {
  const { info, reason, closeUpgrade, refresh } = usePlan();
  const toast = useToast();
  const [interval, setInterval] = useState<'month' | 'year'>(info?.interval ?? 'month');
  const [busy, setBusy] = useState<PlanId | null>(null);
  if (reason === undefined || !info) return null;

  async function choose(plan: PlanId) {
    setBusy(plan);
    try {
      const quote = await api<{ total: number; seats: number; addons: AddonId[]; quoteKey: string }>(`/billing/quote?plan=${plan}&interval=${interval}`);
      if (info?.hasSubscription && !confirm(`${PLANS[plan].name}: ${quote.total} € pro ${interval === 'year' ? 'Jahr' : 'Monat'} zzgl. MwSt.\nEnthält ${quote.seats} zusätzliche Mitgliederplätze${quote.addons.length ? ` und ${quote.addons.map(id => ADDONS[id].name).join(', ')}` : ''}. Änderungen werden anteilig verrechnet.\nVerbindlich wechseln?`)) return;
      const r = await api<{ url?: string; changed?: boolean }>('/billing/checkout', { method: 'POST', json: { plan, interval, quoteKey: quote.quoteKey } });
      if (r.url) location.href = r.url;
      else {
        toast(`Jetzt im Tarif ${PLANS[plan].name}`);
        refresh();
        closeUpgrade();
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Hat nicht geklappt');
    } finally {
      setBusy(null);
    }
  }

  const current = info.plan;
  return (
    <Dialog title={reason ? 'Dafür braucht es einen größeren Tarif' : 'Tarife'} onClose={closeUpgrade} wide>
      <div className="flex flex-col gap-4">
        {reason && (
          <div className="upgrade-reason">
            <Icon name="lock" size={18} />
            <span>
              {reason.message}
              {reason.needs && reason.needs !== current && <> Mit <b>{PLANS[reason.needs].name}</b> geht es weiter.</>}
            </span>
          </div>
        )}
        {info.trial && info.trialEndsAt && (
          <p className="m-0 text-[13px] text-[var(--muted)]">
            Ihr testet gerade {PLANS[info.plan].name} bis {new Date(info.trialEndsAt).toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })}. Danach gilt Free, alle Daten bleiben erhalten.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button className="chip" aria-pressed={interval === 'month'} onClick={() => setInterval('month')}>Monatlich</button>
          <button className="chip" aria-pressed={interval === 'year'} onClick={() => setInterval('year')}>Jährlich, 2 Monate geschenkt</button>
        </div>
        <div className="plans">
          {PLAN_ORDER.map((id) => {
            const p = PLANS[id];
            const mine = id === info.paidPlan && !info.trial;
            const recommended = reason?.needs === id;
            return (
              <section key={id} className={`plancard ${recommended ? 'recommended' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <b className="text-[17px]">{p.name}</b>
                  {mine ? <span className="plantag">Dein Tarif</span> : recommended ? <span className="plantag hot">Passt</span> : null}
                </div>
                <small className="block text-[12.5px] text-[var(--muted)]">{p.claim}</small>
                <div className="planprice">
                  {p.price ? (
                    interval === 'month' ? (
                      <><b>{p.price.month} €</b><span>/ Monat</span></>
                    ) : (
                      <><b>{p.price.year} €</b><span>/ Jahr</span></>
                    )
                  ) : (
                    <><b>0 €</b><span>für immer</span></>
                  )}
                </div>
                <ul>
                  {limitLines(p).map((l) => <li key={l}><Icon name="check" size={14} />{l}</li>)}
                  {FEATURES.map((f) => (
                    <li key={f} className={p.features.includes(f) ? '' : 'off'}>
                      <Icon name={p.features.includes(f) ? 'check' : 'x'} size={14} />{FEATURE_DETAIL[f]}
                    </li>
                  ))}
                </ul>
                {id !== 'free' && !mine && (
                  <button
                    className={`btn ${recommended || (!reason && id === 'plus') ? 'btn-primary' : 'btn-ghost'} mt-auto`}
                    disabled={!info.billing.ready || !info.billing.canManage || busy !== null}
                    onClick={() => void choose(id)}
                  >
                    {busy === id ? 'Einen Moment …' : `${p.name} wählen`}
                  </button>
                )}
              </section>
            );
          })}
        </div>
        <p className="m-0 text-[12.5px] text-[var(--muted)]">
          {!info.billing.canManage
            ? 'Den Tarif ändern Inhaber oder Admins deines Teams.'
            : !info.billing.ready
              ? 'Die Buchung wird gerade eingerichtet und ist in Kürze möglich.'
              : 'Preise zzgl. MwSt. Monatlich kündbar; bei Kündigung gilt danach Free, alle Daten bleiben erhalten.'}
        </p>
      </div>
    </Dialog>
  );
}
